<template>
    <div>
        <UiSuperPrice
            :price-before="product.priceBeforeDiscount || 0"
            :price-actual="product.price"
            :title="title"
            :title-text="$t('competitorPrice.pilulka').toUpperCase()"
            :lowest-price="product.lowestPrice"
            class="link"
            @click="openModal(`modal-superPrice-${uid}`)"
        />

        <Modal size="lg" modal-class="modal-superPrice" :modal-name="`modal-superPrice-${uid}`" :title="$t('competitorPrice.modalTitle')">
            <div class="modalProduct">
                <div class="modalProduct__info">
                    <div class="modalProduct__picture">
                        <img :src="product.image" :alt="product.name" class="modalProduct__img" />
                    </div>

                    <h3 class="modalProduct__name">
                        {{ product.name }}
                    </h3>
                </div>

                <div class="modalProduct__sale">
                    <div class="modalProduct__sale__title">{{ $t('competitorPrice.sale') }}</div>
                    <div v-if="competitorPrice > product.price" class="modalProduct__sale__price notranslate">
                        {{ competitorPrice > product.price ? $formatCurrency(competitorPrice - product.price) : '--' }}
                    </div>
                </div>

                <div v-if="competitorPrice > product.price" class="modalProduct__prices">
                    <UiSuperPrice
                        :title-text="$t('competitorPrice.competitor')"
                        :price-actual="competitorPrice"
                        :grayscale="true"
                        size="xl"
                        :competitor-prices-concurent="competitorPricesConcurent"
                        :lowest-price="product.lowestPrice"
                    />
                    <div class="modalProduct__prices__pipe" />
                    <UiSuperPrice
                        :title-text="$t('competitorPrice.pilulka').toUpperCase()"
                        :price-actual="product.price"
                        size="xl"
                        :lowest-price="product.lowestPrice"
                    />
                </div>

                <ElementsButtonAddBasket
                    v-if="showAddButton"
                    :add-basket-align="``"
                    class="add-cart__container--lg"
                    button-class="btn-primary w-100"
                    :product="product"
                    :product-id="product.id"
                    :product-price="product.price"
                    :is-preorder="product.isPreorder"
                    :max-amount-per-order="product.attributes.maxAmountPerOrder"
                    :has-substitution-modal="product.substitutions?.isModal || false"
                />
                <div class="modalProduct__date">{{ $t('competitorPrice.date') }} {{ competitorPriceDate }}</div>
            </div>
        </Modal>
    </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import Product from '~/models/Product/Product'

// @ts-ignore
const uid = getCurrentInstance().uid
const competitorPrice = ref(0)
const competitorPriceName = ref()
const competitorPriceDate = ref()
const competitorPricesConcurent = ref()

const props = defineProps({
    title: {
        type: Boolean,
        default: true,
    },
    showAddButton: {
        type: Boolean,
        default: false,
    },
    product: {
        type: Object as PropType<Product>,
        default: null,
    },
})
const config = useRuntimeConfig()
const callServer = async () => {
    const requestData = ref({
        id: props.product.id || null,
    })

    // @ts-ignore
    const { response: data } = await $fetch(`${config.public.apiUrl}/api/v2/product/competitor-price`, {
        method: 'POST',
        body: requestData.value,
    })

    return data
}

const fetchData = async () => {
    const data = await callServer()
    competitorPrice.value = parseInt(data.competitorPrice.averagePrice)
    competitorPriceName.value = data.price
    competitorPriceDate.value = data.competitorPrice.lastUpdatedAt
    competitorPricesConcurent.value = data.competitorPrice.prices
}

const openModal = (id: string) => {
    fetchData()
    useNuxtApp().$bus.$emit('showModal', id)
}
</script>

<style lang="scss" scoped>
.link {
    cursor: help;
}

.modalProduct {
    display: flex;
    flex-direction: column;
    text-align: center;
}

.modalProduct__item {
    display: flex;
    gap: 18px;
    align-items: center;
    margin-bottom: 1rem;

    @media (min-width: 768px) {
        display: block;
        text-align: center;
        order: 1;
    }
}

.modalProduct__sale {
    color: $color-cta;
    text-align: center;
    margin-bottom: 2rem;

    @media (min-width: 768px) {
        order: 3;
    }
}

.modalProduct__sale__title {
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
}

.modalProduct__sale__price {
    font-weight: 700;
    font-size: 32px;
    line-height: 43px;
}

.modalProduct__prices {
    display: flex;
    justify-content: center;
    gap: 1rem;
    align-items: stretch;
    margin-bottom: 2rem;

    @media (min-width: 768px) {
        order: 2;
    }
}

.modalProduct__prices__pipe {
    line-height: 100%;
    width: 1px;
    border-left: 1px solid $color-grey-dark;
    margin: 0 0 13px;

    @media (min-width: 768px) {
        margin: 0 50px 13px;
    }
}

.modalProduct {
    &__info {
        @media (max-width: 768px) {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}

.modalProduct__picture {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 65px;
    height: 65px;
    background-color: $color-grey;
    border-radius: $rounded;

    @media (min-width: 768px) {
        display: block;
        text-align: center;
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: 300px;
        background-color: transparent;
        margin-bottom: 1rem;
    }
}

.modalProduct__img {
    max-width: 45px;
    max-height: 45px;

    @media (min-width: 768px) {
        max-width: 100%;
        max-height: 300px;
    }
}

.modalProduct__name {
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    flex: 1;
    margin-left: 20px;
    text-align: left;

    @media (min-width: 768px) {
        font-size: 24px;
        line-height: 32px;
        margin-bottom: 50px;
        margin-left: 0;
        text-align: center;
    }
}

.add-cart__container {
    margin: 0 auto 1.5rem auto;

    @media (min-width: 768px) {
        order: 4;
    }
}

.modalProduct__date {
    text-align: center;
    font-size: 12px;
    color: $color-grey-extra-dark;

    @media (min-width: 768px) {
        order: 5;
    }
}
</style>
